import React, { useState, useEffect, useCallback } from "react";
import {
  DocumentsIcon,
  PrintIcon,
  DeleteIcon,
  CalendarIcon,
  UserIcon,
  AlertCircleIcon,
} from "../Icons";

const formTypes = {
  tbHistory: "TB Test Form",
  fluHistory: "Flu Vaccination Form",
  healthHistory: "Employee Health Examination",
  routeHistory: "Route Sheet",
  consentHistory: "Patient Consent",
  medicareHistory: "Medicare Non-Coverage",
};

const SubmittedDocuments = () => {
  const [allSubmissions, setAllSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [submissionToDelete, setSubmissionToDelete] = useState(null);

  const loadAllSubmissions = useCallback(() => {
    const submissions = [];

    // Load all form histories
    Object.keys(formTypes).forEach((key) => {
      const history = JSON.parse(localStorage.getItem(key) || "[]");
      history.forEach((entry) => {
        submissions.push({
          ...entry,
          formType: formTypes[key],
          formKey: key,
        });
      });
    });

    setAllSubmissions(submissions);
  }, []); // formTypes is now a constant outside component, no dependencies needed

  const filterAndSortSubmissions = useCallback(() => {
    let filtered = [...allSubmissions];

    // Filter by type
    if (filterType !== "all") {
      filtered = filtered.filter((sub) => sub.formKey === filterType);
    }

    // Filter by search term
    if (searchTerm && searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        (sub) =>
          (sub.employeeName && sub.employeeName.toLowerCase().includes(term)) ||
          (sub.formType && sub.formType.toLowerCase().includes(term)) ||
          (sub.employeeId && sub.employeeId.toLowerCase().includes(term)),
      );
    }

    // Sort by date (most recent first) by default
    filtered.sort((a, b) => {
      const aVal = new Date(a.submittedAt || 0).getTime();
      const bVal = new Date(b.submittedAt || 0).getTime();
      return bVal - aVal; // Descending order (newest first)
    });

    setFilteredSubmissions(filtered);
  }, [allSubmissions, filterType, searchTerm]);

  useEffect(() => {
    loadAllSubmissions();
  }, [loadAllSubmissions]);

  useEffect(() => {
    filterAndSortSubmissions();
  }, [filterAndSortSubmissions]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return dateString;
    }
  };

  // Helper functions for date formatting
  const formatDateUS = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    } catch {
      return "";
    }
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return "";
    try {
      let date;
      if (
        typeof dateString === "string" &&
        /^\d{4}-\d{2}-\d{2}$/.test(dateString)
      ) {
        const [year, month, day] = dateString.split("-").map(Number);
        date = new Date(year, month - 1, day);
      } else {
        date = new Date(dateString);
      }
      if (isNaN(date.getTime())) return "";
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const year = date.getFullYear();
      return `${month} / ${day} / ${year}`;
    } catch {
      return "";
    }
  };

  const handlePrint = (submission) => {
    const printHTML = generatePrintHTML(submission);
    const printWindow = window.open("", "_blank");
    printWindow.document.write(printHTML);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handleDeleteClick = (submission) => {
    setSubmissionToDelete(submission);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    if (submissionToDelete) {
      const history = JSON.parse(
        localStorage.getItem(submissionToDelete.formKey) || "[]",
      );
      const updatedHistory = history.filter(
        (entry) => entry.id !== submissionToDelete.id,
      );
      localStorage.setItem(
        submissionToDelete.formKey,
        JSON.stringify(updatedHistory),
      );
      loadAllSubmissions();
      setShowDeleteModal(false);
      setSubmissionToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setSubmissionToDelete(null);
  };

  // Generate Patient Consent Print HTML
  const generateConsentPrintFormHTML = (data) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Patient Consent - ${data.patientName || "Patient"}</title>
        <style>
          @media print {
            @page { 
              margin: 0.4in;
              size: letter;
            }
            body { 
              margin: 0; 
              padding: 0;
            }
          }
          * {
            box-sizing: border-box;
          }
          body {
            font-family: 'Arial', 'Helvetica', sans-serif;
            font-size: 7.5pt;
            line-height: 1.35;
            padding: 10px;
            color: #333;
          }
          .header {
            position: relative;
            margin-bottom: 12px;
            padding-bottom: 8px;
            border-bottom: 2px solid #2c3e50;
            min-height: 80px;
          }
          .logo-container {
            position: absolute;
            top: 0;
            left: 0;
          }
          .header-content {
            text-align: center;
            padding-top: 18px;
          }
          .header h1 {
            font-size: 14pt;
            font-weight: bold;
            margin: 0;
            color: #2c3e50;
            letter-spacing: 1px;
            text-transform: uppercase;
          }
          .contact-info {
            position: absolute;
            top: 0;
            right: 0;
            font-size: 7.5pt;
            text-align: right;
            line-height: 1.3;
          }
          .consent-section {
            margin-bottom: 10px;
            font-size: 7.5pt;
            line-height: 1.35;
          }
          .consent-checkbox {
            display: inline-block;
            width: 12px;
            height: 12px;
            border: 1px solid #333;
            margin-right: 6px;
            vertical-align: middle;
            position: relative;
          }
          .consent-checkbox.checked::after {
            content: "✓";
            position: absolute;
            top: -2px;
            left: 1px;
            font-size: 10px;
            color: #333;
          }
          .consent-title {
            font-weight: bold;
            margin-bottom: 6px;
            color: #2c3e50;
          }
          .consent-text {
            margin-left: 20px;
            margin-bottom: 6px;
            text-align: justify;
            padding-right: 10px;
          }
          .visit-frequency {
            display: flex;
            gap: 15px;
            margin: 8px 0;
            font-size: 7.5pt;
            flex-wrap: wrap;
          }
          .visit-item {
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .visit-label {
            font-weight: 600;
            color: #495057;
            white-space: nowrap;
          }
          .visit-value {
            border-bottom: 1px solid #333;
            min-width: 100px;
            min-height: 18px;
            padding-bottom: 3px;
          }
          .certification-list {
            margin-left: 20px;
            margin-top: 6px;
            margin-bottom: 6px;
          }
          .cert-item {
            margin-bottom: 4px;
            line-height: 1.35;
            font-size: 7pt;
          }
          .signature-section {
            margin-top: 12px;
            padding-top: 10px;
            border-top: 1px solid #333;
          }
          .signature-row {
            display: flex;
            gap: 25px;
            margin-bottom: 10px;
            font-size: 7.5pt;
          }
          .signature-field {
            flex: 1;
          }
          .signature-field.half {
            flex: 0 0 calc(50% - 10px);
          }
          .signature-label {
            font-weight: 600;
            color: #495057;
            margin-bottom: 4px;
            font-size: 7.5pt;
          }
          .signature-value {
            border-bottom: 1px solid #333;
            min-height: 18px;
            padding-bottom: 3px;
            color: #212529;
          }
          .checkbox-inline {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            margin-right: 12px;
            font-size: 7.5pt;
          }
          input[type="checkbox"], input[type="radio"] {
            width: 10px;
            height: 10px;
            margin: 0;
            vertical-align: middle;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo-container">
            <img src="/logo.png.png" alt="Kornivus" style="height: 90px; width: auto; object-fit: contain; max-width: 150px;" onerror="this.style.display='none';" />
          </div>
          <div class="contact-info">
            <div><strong>Infinite Home Health, Inc.</strong></div>
            <div>22151 Ventura Blvd, #201</div>
            <div>Woodland Hills, CA 91364</div>
            <div>Tel: (818) 888-7772</div>
            <div>Fax: (818) 888-7773</div>
          </div>
          <div class="header-content">
            <h1>Patient Consent</h1>
          </div>
        </div>

        <div class="consent-section">
          <div class="consent-title">
            <span class="consent-checkbox ${data.consentAdmission ? "checked" : ""}"></span>
            REQUEST FOR ADMISSION AND CONSENT TO TREATMENT
          </div>
          <div class="consent-text">
            I consent to initial evaluation for eligibility and admission to services and treatment as ordered by my physician through Infinite Home Health. I understand that my care is directed and monitored by my physician and that Infinite Home Health is not liable for any acts or omissions when following the instructions of my physician, as physicians are neither employees nor agents of Infinite Home Health.
          </div>
        </div>

        <div class="consent-section">
          <div class="consent-title">
            <span class="consent-checkbox ${data.consentReleaseInfo ? "checked" : ""}"></span>
            AUTHORIZATION TO RELEASE INFORMATION
          </div>
          <div class="consent-text">
            I consent to the release of information from any hospital, skilled nursing facility, or home care agency where I have been a patient. I authorize health care professionals to disclose medical records to Infinite Home Health and authorize Infinite Home Health to share health status information with other health agencies, professionals involved in my care, or State, Federal, and other accrediting bodies for record review. I understand that I may request transfer to another HHA at any time.
          </div>
        </div>

        <div class="consent-section">
          <div class="consent-title">
            <span class="consent-checkbox ${data.consentCertification ? "checked" : ""}"></span>
            PATIENTS CERTIFICATION, AUTHORIZATION TO RELEASE INFORMATION, AND PAYMENT REQUEST
          </div>
          <div class="consent-text">
            I certify that the information given to Infinite Home Health for payment from CMS is true and correct. I authorize the release of all medical records allowed under HIPAA regulations and authorize payment on my behalf to Infinite Home Health.
          </div>
        </div>

        <div class="consent-section">
          <div class="consent-title">
            <span class="consent-checkbox ${data.consentBilling ? "checked" : ""}"></span>
            HOME HEALTH CONSOLIDATED BILLING UNDER PPS
          </div>
          <div class="consent-text">
            I understand that services subject to home health consolidated billing include SN care, CHHA, PT, ST, OT, MSW routine and non-routine medical supplies, and care for homebound patients involving cumbersome equipment. DME is excluded. I was advised in advance by the HHA about consolidated billing expectations from Medicare or other sources, including myself.
          </div>
        </div>

        <div class="consent-section">
          <div class="consent-title">
            <span class="consent-checkbox ${data.consentInsurance ? "checked" : ""}"></span>
            ASSIGNMENT OF INSURANCE BENEFITS
          </div>
          <div class="consent-text">
            I authorize direct payment of insurance benefits to Infinite Home Health for services at a rate not exceeding regular charges. I understand that I am responsible for charges not covered by my insurance plan. Medicare covers all Home Health billable expenses.
          </div>
        </div>

        <div class="consent-section">
          <div class="consent-title">
            <span class="consent-checkbox ${data.consentCMSAdvance ? "checked" : ""}"></span>
            CMS ADVANCE BENEFICIARY NOTICE
          </div>
          <div class="consent-text">
            I agree to individually obligate myself to pay Infinite Home Health for services according to regular rates and terms. If this account is referred to an attorney for collection, I will pay reasonable collection expenses, including attorney fees.
          </div>
          <div style="margin-left: 20px; margin-top: 4px;">
            <span class="checkbox-inline">
              <input type="checkbox" ${data.cmsPrivateInsurance ? "checked" : ""} disabled>
              <span style="font-weight: bold;">PRIVATE INSURANCE</span>
            </span>
            <span class="checkbox-inline">
              <input type="checkbox" ${data.cmsSelfPay ? "checked" : ""} disabled>
              <span style="font-weight: bold;">SELF PAY</span>
            </span>
          </div>
        </div>

        <div class="consent-section">
          <div class="consent-title" style="margin-bottom: 6px;">Visit Frequency:</div>
          <div class="visit-frequency">
            <div class="visit-item">
              <span class="visit-label">SN Visits:</span>
              <span class="visit-value">${data.visitFrequencySN || "________________"}</span>
            </div>
            <div class="visit-item">
              <span class="visit-label">CHHA Visits:</span>
              <span class="visit-value">${data.visitFrequencyCHHA || "________________"}</span>
            </div>
            <div class="visit-item">
              <span class="visit-label">PT:</span>
              <span class="visit-value">${data.visitFrequencyPT || "________________"}</span>
            </div>
            <div class="visit-item">
              <span class="visit-label">OT:</span>
              <span class="visit-value">${data.visitFrequencyOT || "________________"}</span>
            </div>
            <div class="visit-item">
              <span class="visit-label">ST:</span>
              <span class="visit-value">${data.visitFrequencyST || "________________"}</span>
            </div>
            <div class="visit-item">
              <span class="visit-label">MSW:</span>
              <span class="visit-value">${data.visitFrequencyMSW || "________________"}</span>
            </div>
          </div>
        </div>

        <div class="consent-section">
          <div class="consent-title">
            <span class="consent-checkbox ${data.certPatientRights || data.certOasisPrivacy || data.certHotlineInfo || data.certPrivacyRights || data.certPlanOfCare || data.certRates || data.certTollFree || data.certEmergencyPlan || data.certBrochure || data.certDocumentCopy || data.certAdvanceDirectives ? "checked" : ""}"></span>
            CERTIFICATION
          </div>
          <div class="certification-list">
            <div class="cert-item">
              <span class="consent-checkbox ${data.certPatientRights ? "checked" : ""}"></span>
              I certify that I have received a copy and have been educated on the Patient's Rights and Responsibilities.
            </div>
            <div class="cert-item">
              <span class="consent-checkbox ${data.certOasisPrivacy ? "checked" : ""}"></span>
              I certify that I have received a copy and have been educated on the Patient's oasis privacy Rights and HIPPA regulations.
            </div>
            <div class="cert-item">
              <span class="consent-checkbox ${data.certHotlineInfo ? "checked" : ""}"></span>
              I certify that I have received the State and Joint Commission hotline information
            </div>
            <div class="cert-item">
              <span class="consent-checkbox ${data.certPrivacyRights ? "checked" : ""}"></span>
              I have been informed about the patient's privacy rights, and on how to file a complaint.
            </div>
            <div class="cert-item">
              <span class="consent-checkbox ${data.certPlanOfCare ? "checked" : ""}"></span>
              The Initial Plan of Care has been reviewed with me.
            </div>
            <div class="cert-item">
              <span class="consent-checkbox ${data.certRates ? "checked" : ""}"></span>
              The Home Health rates have been reviewed with me.
            </div>
            <div class="cert-item">
              <span class="consent-checkbox ${data.certTollFree ? "checked" : ""}"></span>
              I have been informed about the State and Joint Commission Home Health toll free hotline.
            </div>
            <div class="cert-item">
              <span class="consent-checkbox ${data.certEmergencyPlan ? "checked" : ""}"></span>
              I certify I have been advised and received written instructions regarding and Emergency Plan and Safety for my care.
            </div>
            <div class="cert-item">
              <span class="consent-checkbox ${data.certBrochure ? "checked" : ""}"></span>
              I have received a brochure about Infinite Home Health including the Non-Discriminatory Notice in the handbook.
            </div>
            <div class="cert-item">
              <span class="consent-checkbox ${data.certDocumentCopy ? "checked" : ""}"></span>
              I have received a copy of this document.
            </div>
            <div class="cert-item">
              <span class="consent-checkbox ${data.certAdvanceDirectives ? "checked" : ""}"></span>
              I have formulated Advance Directives with my legal advisor or my family.
              <span style="margin-left: 8px;">
                <span class="checkbox-inline">
                  <input type="checkbox" ${data.advanceDirectivesYes ? "checked" : ""} disabled>
                  <span>Yes</span>
                </span>
                <span class="checkbox-inline">
                  <input type="checkbox" ${data.advanceDirectivesNo ? "checked" : ""} disabled>
                  <span>No</span>
                </span>
              </span>
            </div>
          </div>
        </div>

        <div class="consent-section">
          <div class="consent-title">
            <span class="consent-checkbox ${data.consentPhotoRecords ? "checked" : ""}"></span>
            I AUTHORIZE INFINITE HOME HEALTH TO OBTAIN PHOTO RECORDS OF WOUND(S) DURING SERVICE.
          </div>
        </div>

        <div class="signature-section">
          <div class="signature-row">
            <div class="signature-field">
              <div class="signature-label">Signature:</div>
              <div class="signature-value">${data.patientSignature || "________________________________________________________________________________"}</div>
            </div>
          </div>
          <div class="signature-row">
            <div class="signature-field half">
              <div class="signature-label">Patient's Name:</div>
              <div class="signature-value">${data.patientNamePrinted || data.patientName || "________________________________________________________________________________"}</div>
            </div>
            <div class="signature-field half">
              <div class="signature-label">Date:</div>
              <div class="signature-value">${formatDateUS(data.signatureDate || data.consentDate) || "________________"}</div>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // Generate Employee Health Examination Print HTML
  const generateHealthPrintFormHTML = (data) => {
    // Ensure familyHistory is an array
    const familyHistory = Array.isArray(data.familyHistory)
      ? data.familyHistory
      : [];

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Employee Health Examination - ${data.employeeName || "Employee"}</title>
        <style>
          @media print {
            @page { 
              margin: 0.4in;
              size: letter;
            }
            body { 
              margin: 0; 
              padding: 0;
            }
          }
          * {
            box-sizing: border-box;
          }
          body {
            font-family: 'Arial', 'Helvetica', sans-serif;
            font-size: 8pt;
            line-height: 1.25;
            padding: 10px;
            color: #333;
          }
          .header {
            position: relative;
            margin-bottom: 8px;
            padding-bottom: 8px;
            border-bottom: 3px solid #2c3e50;
            min-height: 90px;
          }
          .logo-container {
            position: absolute;
            top: 0;
            left: 0;
          }
          .header-content {
            text-align: center;
            padding-top: 15px;
          }
          .header h1 {
            font-size: 16pt;
            font-weight: bold;
            margin: 0;
            color: #2c3e50;
            letter-spacing: 1px;
            text-transform: uppercase;
          }
          .header .subtitle {
            font-size: 9pt;
            font-style: italic;
            color: #666;
            margin: 4px 0 0 0;
          }
          .contact-info {
            position: absolute;
            top: 0;
            right: 0;
            font-size: 8pt;
            text-align: right;
            line-height: 1.4;
          }
          .physician-statement {
            margin-bottom: 8px;
            padding: 6px 0;
          }
          .statement-text {
            font-size: 8pt;
            line-height: 1.4;
            margin-bottom: 4px;
            color: #333;
          }
          .statement-line {
            border-bottom: 1px solid #333;
            min-height: 14px;
            display: inline-block;
            min-width: 100px;
            margin: 0 3px;
          }
          .statement-blank {
            border-bottom: 1px solid #333;
            min-height: 16px;
            display: block;
            margin-top: 2px;
            width: 100%;
            font-size: 8pt;
          }
          .divider {
            border-top: 2px solid #333;
            margin: 6px 0;
          }
          .section-title {
            font-size: 10pt;
            font-weight: bold;
            margin: 8px 0 6px 0;
            color: #2c3e50;
            text-transform: uppercase;
            text-align: center;
            letter-spacing: 0.5px;
          }
          .examination-grid {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr 1fr;
            gap: 10px;
            margin-bottom: 8px;
          }
          .examination-item {
            display: flex;
            flex-direction: column;
          }
          .examination-label {
            font-weight: 600;
            color: #495057;
            margin-bottom: 3px;
            font-size: 9pt;
          }
          .examination-value {
            border-bottom: 1px solid #495057;
            min-height: 20px;
            padding-bottom: 2px;
            color: #212529;
            font-size: 9pt;
          }
          .family-history-box {
            border: 2px solid #333;
            padding: 6px;
            margin-bottom: 8px;
          }
          .family-history-title {
            font-weight: bold;
            font-size: 9.5pt;
            margin-bottom: 6px;
            color: #2c3e50;
          }
          .family-history-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 6px;
          }
          .family-history-table th {
            border: 1px solid #333;
            padding: 4px 6px;
            background-color: #f0f0f0;
            font-weight: bold;
            font-size: 8.5pt;
            text-align: left;
          }
          .family-history-table td {
            border: 1px solid #333;
            padding: 4px 6px;
            font-size: 8.5pt;
            min-height: 20px;
          }
          .physical-exam-section {
            margin-bottom: 8px;
          }
          .test-row {
            display: flex;
            align-items: center;
            gap: 6px;
            margin-bottom: 6px;
            font-size: 8pt;
          }
          .test-label {
            font-weight: 600;
            color: #495057;
            min-width: 100px;
          }
          .checkbox-group {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            margin-right: 12px;
          }
          .checkbox-label {
            font-size: 9pt;
            color: #495057;
          }
          .two-column-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
            margin-bottom: 8px;
          }
          .body-system-item {
            margin-bottom: 4px;
            font-size: 8pt;
          }
          .body-system-label {
            font-weight: 600;
            color: #495057;
            display: inline-block;
            min-width: 80px;
          }
          .body-system-value {
            border-bottom: 1px solid #495057;
            min-height: 18px;
            display: inline-block;
            min-width: 150px;
            margin-left: 6px;
            padding-bottom: 2px;
            color: #212529;
          }
          .cardiopulmonary-item {
            margin-bottom: 4px;
            font-size: 8pt;
          }
          .cardiopulmonary-label {
            font-weight: 600;
            color: #495057;
            display: inline-block;
            min-width: 100px;
          }
          .cardiopulmonary-value {
            border-bottom: 1px solid #495057;
            min-height: 18px;
            display: inline-block;
            min-width: 120px;
            margin-left: 6px;
            padding-bottom: 2px;
            color: #212529;
          }
          .physician-info {
            margin-top: 8px;
            padding-top: 6px;
          }
          .physician-row {
            display: flex;
            gap: 12px;
            margin-bottom: 6px;
            font-size: 8pt;
          }
          .physician-label {
            font-weight: 600;
            color: #495057;
            min-width: 140px;
          }
          .physician-value {
            border-bottom: 1px solid #495057;
            flex: 1;
            min-height: 18px;
            padding-bottom: 2px;
            color: #212529;
          }
          input[type="checkbox"], input[type="radio"] {
            width: 12px;
            height: 12px;
            margin: 0;
            vertical-align: middle;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo-container">
            <img src="/logo.png.png" alt="Kornivus" style="height: 90px; width: auto; object-fit: contain; max-width: 150px;" onerror="this.style.display='none';" />
          </div>
          <div class="contact-info">
            <div>Phone: 818-888-7772</div>
            <div>Fax: 818-888-7773</div>
            <div>22151 Ventura Blvd., #201</div>
            <div>Woodland Hills, Ca, 91364</div>
          </div>
          <div class="header-content">
            <h1>Employee Health Examination</h1>
            <p class="subtitle">(To be completed by the employee's physician)</p>
          </div>
        </div>

        <div class="physician-statement">
          <div class="statement-text">
            I have examined <span class="statement-line">${(data.patientTitle || "") + " " + (data.patientName || data.employeeName || "________________")}</span> 
            of: <span class="statement-line">${data.positionFull || "________________"}</span> 
            who is applying for the position <span class="statement-line">${data.positionFull || "________________"}</span> 
            and have found no condition that appears to prevent him/her from performing the duties of the position applied for, 
            with the exception or possible exception of the following: 
          </div>
          <div class="statement-blank">${data.conditionException || "None"}</div>
          
          <div class="statement-text" style="margin-top: 6px;">
            I have found no indication of any condition which might represent a possible hazard to the health of the patients 
            or other employees of this facility. He/she is free from communicable diseases.
          </div>
          <div class="statement-blank">${data.noHazardStatement || "None"}</div>
        </div>

        <div class="divider"></div>

        <div class="section-title">EXAMINATION</div>
        <div class="examination-grid">
          <div class="examination-item">
            <div class="examination-label">Height:</div>
            <div class="examination-value">${data.height || "________________"}</div>
          </div>
          <div class="examination-item">
            <div class="examination-label">Weight:</div>
            <div class="examination-value">${data.weight || "________________"}</div>
          </div>
          <div class="examination-item">
            <div class="examination-label">Blood Pressure:</div>
            <div class="examination-value">${data.bloodPressure || "________________"}</div>
          </div>
          <div class="examination-item">
            <div class="examination-label">Pulse:</div>
            <div class="examination-value">${data.pulse || "________________"}</div>
          </div>
        </div>

        <div class="family-history-box">
          <div class="family-history-title">
            Family History: Any significant illness in the family? If so, please state the illness and relationship.
          </div>
          <table class="family-history-table">
            <thead>
              <tr>
                <th>Family Member</th>
                <th>Illness</th>
                <th>Relationship</th>
              </tr>
            </thead>
            <tbody>
              ${familyHistory
        .map(
          (fh) => `
                <tr>
                  <td>${fh.member || ""}</td>
                  <td>${fh.illness || ""}</td>
                  <td>${fh.relationship || ""}</td>
                </tr>
              `,
        )
        .join("")}
              ${Array(Math.max(0, 3 - familyHistory.length))
        .fill(0)
        .map(
          () => `
                <tr>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              `,
        )
        .join("")}
            </tbody>
          </table>
        </div>

        <div class="divider"></div>

        <div class="section-title">PHYSICAL EXAMINATION</div>
        
        <div class="physical-exam-section">
          <div class="test-row">
            <span class="test-label">PPD Test:</span>
            <div class="checkbox-group">
              <input type="checkbox" ${data.ppdTestResult === "Negative" ? "checked" : ""} disabled>
              <span class="checkbox-label">Negative</span>
            </div>
            <div class="checkbox-group">
              <input type="checkbox" ${data.ppdTestResult === "Positive" ? "checked" : ""} disabled>
              <span class="checkbox-label">Positive</span>
            </div>
            <span style="margin-left: 10px;">Mantoux: <span class="body-system-value" style="min-width: 100px;">${data.ppdMantoux || "______"}</span></span>
            <span style="margin-left: 10px;">Date Administered: <span class="body-system-value" style="min-width: 100px;">${formatDateShort(data.ppdDateAdministered) || "______"}</span></span>
          </div>
          
          <div class="test-row">
            <span class="test-label">Chest X-Ray (if indicated):</span>
            <div class="checkbox-group">
              <input type="checkbox" ${data.chestXRayResult === "Negative" ? "checked" : ""} disabled>
              <span class="checkbox-label">Negative</span>
            </div>
            <div class="checkbox-group">
              <input type="checkbox" ${data.chestXRayResult === "Positive" ? "checked" : ""} disabled>
              <span class="checkbox-label">Positive</span>
            </div>
            <span style="margin-left: 10px;">Date Administered: <span class="body-system-value" style="min-width: 100px;">${formatDateShort(data.chestXRayDate) || "______"}</span></span>
          </div>
        </div>

        <div class="two-column-grid">
          <div>
            <div class="body-system-item">
              <span class="body-system-label">Adenopathy:</span>
              <span class="body-system-value">${data.adenopathy || ""}</span>
            </div>
            <div class="body-system-item">
              <span class="body-system-label">Reflexes:</span>
              <span class="body-system-value">${data.reflexes || ""}</span>
            </div>
            <div class="body-system-item">
              <span class="body-system-label">Eyes:</span>
              <span class="body-system-value">${data.eyes || ""}</span>
            </div>
            <div class="body-system-item">
              <span class="body-system-label">Hearing:</span>
              <span class="body-system-value">${data.hearing || ""}</span>
            </div>
            <div class="body-system-item">
              <span class="body-system-label">Nose:</span>
              <span class="body-system-value">${data.nose || ""}</span>
            </div>
            <div class="body-system-item">
              <span class="body-system-label">Throat:</span>
              <span class="body-system-value">${data.throat || ""}</span>
            </div>
            <div class="body-system-item">
              <span class="body-system-label">Tongue:</span>
              <span class="body-system-value">${data.tongue || ""}</span>
            </div>
            <div class="body-system-item">
              <span class="body-system-label">Teeth:</span>
              <span class="body-system-value">${data.teeth || ""}</span>
            </div>
            <div class="body-system-item">
              <span class="body-system-label">Abdomen:</span>
              <span class="body-system-value">${data.abdomen || ""}</span>
            </div>
            <div class="body-system-item">
              <span class="body-system-label">Rectal:</span>
              <span class="body-system-value">${data.rectal || ""}</span>
            </div>
          </div>
          <div>
            <div class="cardiopulmonary-item">
              <span class="cardiopulmonary-label">Chest: Breath Sounds:</span>
              <span class="cardiopulmonary-value">${data.chestBreathSounds || ""}</span>
            </div>
            <div class="cardiopulmonary-item">
              <span class="cardiopulmonary-label">Resonance:</span>
              <span class="cardiopulmonary-value">${data.chestResonance || ""}</span>
            </div>
            <div class="cardiopulmonary-item">
              <span class="cardiopulmonary-label">Heart: Size:</span>
              <span class="cardiopulmonary-value">${data.heartSize || ""}</span>
            </div>
            <div class="cardiopulmonary-item">
              <span class="cardiopulmonary-label">Murmur:</span>
              <span class="cardiopulmonary-value">${data.heartMurmur || ""}</span>
            </div>
            <div class="cardiopulmonary-item">
              <span class="cardiopulmonary-label">Rhythm:</span>
              <span class="cardiopulmonary-value">${data.heartRhythm || ""}</span>
            </div>
            <div class="cardiopulmonary-item">
              <span class="cardiopulmonary-label">Arteries:</span>
              <span class="cardiopulmonary-value">${data.arteries || ""}</span>
            </div>
          </div>
        </div>

        <div class="divider"></div>

        <div class="physician-info">
          <div class="physician-row">
            <span class="physician-label">Physician's Name:</span>
            <span class="physician-value">${data.physicianName || "________________"}</span>
            <span class="physician-label" style="min-width: 160px; margin-left: 15px;">Physician's Phone Number:</span>
            <span class="physician-value">${data.physicianPhone || "________________"}</span>
          </div>
          <div class="physician-row">
            <span class="physician-label">Physician's Address:</span>
            <span class="physician-value">${data.physicianAddress || "________________"}</span>
          </div>
          <div class="physician-row">
            <span class="physician-label">Physician's Signature:</span>
            <span class="physician-value">${data.physicianSignature || "________________"}</span>
            <span class="physician-label" style="min-width: 80px; margin-left: 15px;">Date:</span>
            <span class="physician-value">${formatDateShort(data.physicianDate) || "________________"}</span>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // Generate TB Test Print HTML
  const generateTbPrintFormHTML = (data) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Annual Tuberculosis Form - ${data.employeeName || "Employee"}</title>
        <style>
          @media print {
            @page { 
              margin: 0.4in;
              size: letter;
            }
            body { 
              margin: 0; 
              padding: 0;
            }
          }
          * {
            box-sizing: border-box;
          }
          body {
            font-family: 'Arial', 'Helvetica', sans-serif;
            font-size: 9.5pt;
            line-height: 1.3;
            padding: 15px;
            color: #333;
          }
          .header {
            position: relative;
            margin-bottom: 12px;
            padding-bottom: 10px;
            border-bottom: 3px solid #2c3e50;
            min-height: 100px;
          }
          .logo-container {
            position: absolute;
            top: 0;
            left: 0;
          }
          .header-content {
            text-align: center;
            padding-top: 20px;
          }
          .header h1 {
            font-size: 16pt;
            font-weight: bold;
            margin: 0;
            color: #2c3e50;
            letter-spacing: 1px;
            text-transform: uppercase;
          }
          .contact-info {
            position: absolute;
            top: 0;
            right: 0;
            font-size: 8pt;
            text-align: right;
            line-height: 1.4;
          }
          .info-section {
            background-color: transparent;
            border: none;
            border-radius: 0;
            padding: 0;
            margin-bottom: 10px;
          }
          .info-row {
            display: flex;
            margin-bottom: 6px;
          }
          .info-row:last-child {
            margin-bottom: 0;
          }
          .info-label {
            font-weight: 600;
            color: #495057;
            min-width: 200px;
            font-size: 10pt;
          }
          .info-value {
            color: #212529;
            font-size: 10pt;
            border-bottom: 1px solid #495057;
            flex: 1;
            padding-bottom: 0;
            min-height: 20px;
          }
          .form-section {
            margin-bottom: 10px;
            border: none;
            border-radius: 0;
            padding: 8px 0;
            background-color: #ffffff;
          }
          .section-title {
            font-size: 11pt;
            font-weight: bold;
            margin-bottom: 8px;
            color: #2c3e50;
            padding-bottom: 0;
            border-bottom: none;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .question-item {
            margin-bottom: 8px;
            padding-bottom: 0;
            border-bottom: none;
          }
          .question-item:last-child {
            margin-bottom: 0;
            padding-bottom: 0;
          }
          .question-text {
            font-weight: 600;
            color: #495057;
            margin-bottom: 4px;
            font-size: 9.5pt;
          }
          .checkbox-group {
            display: inline-block;
            margin-right: 15px;
            font-size: 9.5pt;
          }
          .checkbox-label {
            margin-left: 4px;
            color: #495057;
          }
          .symptom-list {
            margin-top: 6px;
          }
          .symptom-item {
            margin-bottom: 4px;
            padding: 2px 0;
            font-size: 9.5pt;
            color: #495057;
          }
          .signature-row {
            display: flex;
            gap: 15px;
            margin-top: 8px;
            padding-top: 0;
            border-top: none;
          }
          .signature-field {
            flex: 1;
          }
          .signature-label {
            font-weight: 600;
            color: #495057;
            margin-bottom: 3px;
            font-size: 9.5pt;
          }
          .signature-value {
            border-bottom: 1px solid #495057;
            min-height: 20px;
            padding-bottom: 0;
            color: #212529;
            font-size: 9.5pt;
          }
          .two-column {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
            margin-bottom: 8px;
          }
          .form-field {
            margin-bottom: 8px;
          }
          .form-label {
            font-weight: 600;
            color: #495057;
            display: block;
            margin-bottom: 3px;
            font-size: 9.5pt;
          }
          .form-value {
            border-bottom: 1px solid #495057;
            min-height: 20px;
            padding-bottom: 0;
            color: #212529;
            font-size: 9.5pt;
            display: block;
            width: 100%;
          }
          .inline-field {
            display: flex;
            align-items: center;
            gap: 8px;
          }
          input[type="checkbox"], input[type="radio"] {
            width: 14px;
            height: 14px;
            margin: 0;
            vertical-align: middle;
          }
          .explanation-box {
            margin-top: 6px;
            margin-left: 15px;
            padding: 6px;
            background-color: #f8f9fa;
            border-left: 3px solid #2c3e50;
            font-size: 8.5pt;
            color: #495057;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo-container">
            <img src="/logo.png.png" alt="Kornivus" style="height: 90px; width: auto; object-fit: contain; max-width: 150px;" onerror="this.style.display='none';" />
          </div>
          <div class="contact-info">
            <div>Phone: 818-888-7772</div>
            <div>Fax: 818-888-7773</div>
            <div>22151 Ventura Blvd., #201</div>
            <div>Woodland Hills, Ca, 91364</div>
          </div>
          <div class="header-content">
            <h1>ANNUAL TUBERCULOSIS FORM</h1>
          </div>
        </div>

        <div class="info-section">
          <div class="info-row">
            <span class="info-label">Name:</span>
            <span class="info-value">${data.employeeName || "________________"}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Social Security Number:</span>
            <span class="info-value">${data.socialSecurityNumber || "________________"}</span>
          </div>
        </div>

        <div class="form-section">
          <div class="section-title">TB SCREENING</div>
          
          <div class="question-item">
            <div class="question-text">1. Have you ever had tuberculosis?</div>
            <div>
              <div class="checkbox-group">
                <input type="checkbox" ${data.hadTuberculosis === "Yes" ? "checked" : ""} disabled> 
                <span class="checkbox-label">Yes</span>
              </div>
              <div class="checkbox-group">
                <input type="checkbox" ${data.hadTuberculosis === "No" ? "checked" : ""} disabled> 
                <span class="checkbox-label">No</span>
              </div>
            </div>
            ${data.hadTuberculosis === "Yes" && data.tuberculosisExplanation
        ? `
              <div class="explanation-box">
                <strong>If yes, please explain, including date of positive test, circumstances and treatment involved:</strong><br>
                ${data.tuberculosisExplanation}
              </div>
            `
        : ""
      }
          </div>

          <div class="question-item">
            <div class="question-text">2. Have you ever had the BCG vaccine?</div>
            <div>
              <div class="checkbox-group">
                <input type="checkbox" ${data.hadBcgVaccine === "Yes" ? "checked" : ""} disabled> 
                <span class="checkbox-label">Yes</span>
              </div>
              <div class="checkbox-group">
                <input type="checkbox" ${data.hadBcgVaccine === "No" ? "checked" : ""} disabled> 
                <span class="checkbox-label">No</span>
              </div>
            </div>
            ${data.hadBcgVaccine === "Yes"
        ? `
              <div style="margin-top: 4px;">
                <span style="font-weight: 600; color: #495057; font-size: 9.5pt;">Year received:</span>
                <span class="form-value" style="display: inline-block; min-width: 80px; margin-left: 6px; min-height: 16px;">${data.bcgVaccineYear || ""}</span>
              </div>
            `
        : ""
      }
          </div>

          <div class="question-item">
            <div class="question-text">3. Have you ever had a positive TB skin test?</div>
            <div>
              <div class="checkbox-group">
                <input type="checkbox" ${data.hadPositiveTbTest === "Yes" ? "checked" : ""} disabled> 
                <span class="checkbox-label">Yes</span>
              </div>
              <div class="checkbox-group">
                <input type="checkbox" ${data.hadPositiveTbTest === "No" ? "checked" : ""} disabled> 
                <span class="checkbox-label">No</span>
              </div>
            </div>
            ${data.hadPositiveTbTest === "Yes"
        ? `
              <div style="margin-top: 4px;">
                <span style="font-weight: 600; color: #495057; font-size: 9.5pt;">Date of positive test:</span>
                <span class="form-value" style="display: inline-block; min-width: 100px; margin-left: 6px; min-height: 16px;">${formatDateShort(data.positiveTbTestDate) || ""}</span>
              </div>
              <div class="explanation-box" style="margin-top: 4px;">
                <strong>If you were treated please include the dates treated and type of treatment:</strong><br>
                ${data.positiveTbTestTreatment || ""}
              </div>
            `
        : ""
      }
          </div>

          <div class="question-item">
            <div class="question-text">4. Do you currently have any of the following symptoms?</div>
            <div class="symptom-list">
              <div class="symptom-item">
                <span>Productive or persistent cough (over 2 weeks duration)</span>
                <div class="checkbox-group" style="margin-left: 15px;">
                  <input type="checkbox" ${data.symptomCough === "Yes" ? "checked" : ""} disabled> 
                  <span class="checkbox-label">Yes</span>
                </div>
                <div class="checkbox-group">
                  <input type="checkbox" ${data.symptomCough === "No" ? "checked" : ""} disabled> 
                  <span class="checkbox-label">No</span>
                </div>
              </div>
              <div class="symptom-item">
                <span>Night sweats</span>
                <div class="checkbox-group" style="margin-left: 15px;">
                  <input type="checkbox" ${data.symptomNightSweats === "Yes" ? "checked" : ""} disabled> 
                  <span class="checkbox-label">Yes</span>
                </div>
                <div class="checkbox-group">
                  <input type="checkbox" ${data.symptomNightSweats === "No" ? "checked" : ""} disabled> 
                  <span class="checkbox-label">No</span>
                </div>
              </div>
              <div class="symptom-item">
                <span>Fever</span>
                <div class="checkbox-group" style="margin-left: 15px;">
                  <input type="checkbox" ${data.symptomFever === "Yes" ? "checked" : ""} disabled> 
                  <span class="checkbox-label">Yes</span>
                </div>
                <div class="checkbox-group">
                  <input type="checkbox" ${data.symptomFever === "No" ? "checked" : ""} disabled> 
                  <span class="checkbox-label">No</span>
                </div>
              </div>
              <div class="symptom-item">
                <span>Weight loss</span>
                <div class="checkbox-group" style="margin-left: 15px;">
                  <input type="checkbox" ${data.symptomWeightLoss === "Yes" ? "checked" : ""} disabled> 
                  <span class="checkbox-label">Yes</span>
                </div>
                <div class="checkbox-group">
                  <input type="checkbox" ${data.symptomWeightLoss === "No" ? "checked" : ""} disabled> 
                  <span class="checkbox-label">No</span>
                </div>
              </div>
              <div class="symptom-item">
                <span>Loss of Appetite</span>
                <div class="checkbox-group" style="margin-left: 15px;">
                  <input type="checkbox" ${data.symptomLossOfAppetite === "Yes" ? "checked" : ""} disabled> 
                  <span class="checkbox-label">Yes</span>
                </div>
                <div class="checkbox-group">
                  <input type="checkbox" ${data.symptomLossOfAppetite === "No" ? "checked" : ""} disabled> 
                  <span class="checkbox-label">No</span>
                </div>
              </div>
            </div>
          </div>

          <div class="signature-row">
            <div class="signature-field">
              <div class="signature-label">Signature:</div>
              <div class="signature-value">${data.screeningSignature || "________________"}</div>
            </div>
            <div class="signature-field">
              <div class="signature-label">Date:</div>
              <div class="signature-value">${formatDateShort(data.screeningDate) || "________________"}</div>
            </div>
          </div>
        </div>

        <div class="form-section">
          <div class="section-title">TB SKIN TEST</div>
          
          <div class="two-column">
            <div class="form-field">
              <div class="form-label">Healthcare Professional Name:</div>
              <div class="form-value">${data.healthcareProfessionalName || "________________"}</div>
            </div>
            <div class="form-field">
              <div class="form-label">Date Placed:</div>
              <div class="form-value">${formatDateShort(data.datePlaced) || "____/____/____"}</div>
            </div>
          </div>
          <div class="two-column">
            <div class="form-field">
              <div class="form-label">Site:</div>
              <div>
                <div class="checkbox-group">
                  <input type="radio" ${data.testSite === "Right" ? "checked" : ""} disabled> 
                  <span class="checkbox-label">Right</span>
                </div>
                <div class="checkbox-group">
                  <input type="radio" ${data.testSite === "Left" ? "checked" : ""} disabled> 
                  <span class="checkbox-label">Left</span>
                </div>
              </div>
            </div>
            <div class="form-field">
              <div class="form-label">Date Read:</div>
              <div class="form-value">${formatDateShort(data.dateRead) || "____/____/____"}</div>
            </div>
          </div>
          <div class="two-column">
            <div class="form-field">
              <div class="form-label">Induration:</div>
              <div class="inline-field">
                <span class="form-value" style="min-width: 80px;">${data.induration || "____"}</span>
                <span style="color: #495057; font-size: 10pt;">mm</span>
              </div>
            </div>
            <div class="form-field">
              <div class="form-label">Lot #:</div>
              <div class="form-value">${data.lotNumber || "________________"}</div>
            </div>
          </div>
          <div class="two-column">
            <div class="form-field">
              <div class="form-label">Exp (Expiration):</div>
              <div class="form-value">${formatDateShort(data.expiration) || "________________"}</div>
            </div>
            <div class="form-field">
              <div class="form-label">PPD Result:</div>
              <div>
                <div class="checkbox-group">
                  <input type="radio" ${data.ppdResult === "(+)" ? "checked" : ""} disabled> 
                  <span class="checkbox-label">(+)</span>
                </div>
                <div class="checkbox-group">
                  <input type="radio" ${data.ppdResult === "(-)" ? "checked" : ""} disabled> 
                  <span class="checkbox-label">(-)</span>
                </div>
              </div>
            </div>
          </div>
          <div class="two-column">
            <div class="form-field">
              <div class="form-label">Administered by:</div>
              <div class="form-value">${data.administeredByName || "________________"}</div>
              <div class="form-label" style="margin-top: 4px;">Title:</div>
              <div class="form-value">${data.administeredByTitle || "________________"}</div>
            </div>
            <div class="form-field">
              <div class="form-label">Results Read by:</div>
              <div class="form-value">${data.resultsReadByName || "________________"}</div>
              <div class="form-label" style="margin-top: 4px;">Title:</div>
              <div class="form-value">${data.resultsReadByTitle || "________________"}</div>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // Generate Flu Vaccination Print HTML
  const generateFluPrintFormHTML = (data) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Flu Vaccination Program - ${data.employeeName || "Employee"}</title>
        <style>
          @media print {
            @page { 
              margin: 0.4in;
              size: letter;
            }
            body { 
              margin: 0; 
              padding: 0;
            }
          }
          * {
            box-sizing: border-box;
          }
          body {
            font-family: 'Arial', 'Helvetica', sans-serif;
            font-size: 8.5pt;
            line-height: 1.3;
            padding: 12px;
            color: #333;
          }
          .header {
            position: relative;
            margin-bottom: 12px;
            padding-bottom: 10px;
            border-bottom: 3px solid #2c3e50;
            min-height: 100px;
          }
          .logo-container {
            position: absolute;
            top: 0;
            left: 0;
          }
          .header-content {
            text-align: center;
            padding-top: 20px;
          }
          .header h1 {
            font-size: 16pt;
            font-weight: bold;
            margin: 0;
            color: #2c3e50;
            letter-spacing: 1px;
            text-transform: uppercase;
          }
          .contact-info {
            position: absolute;
            top: 0;
            right: 0;
            font-size: 8pt;
            text-align: right;
            line-height: 1.4;
          }
          .section {
            margin-bottom: 14px;
          }
          .section-title {
            font-size: 10.5pt;
            font-weight: bold;
            margin: 12px 0 8px 0;
            color: #2c3e50;
            text-transform: uppercase;
            text-align: center;
          }
          .divider {
            border-top: 2px dashed #333;
            margin: 12px 0;
            text-align: center;
            padding-top: 6px;
          }
          .divider-text {
            font-size: 8pt;
            color: #666;
          }
          .info-row {
            display: flex;
            margin-bottom: 8px;
            font-size: 8.5pt;
          }
          .info-label {
            font-weight: 600;
            color: #495057;
            min-width: 140px;
          }
          .info-value {
            border-bottom: 1px solid #495057;
            flex: 1;
            min-height: 18px;
            padding-bottom: 2px;
            color: #212529;
          }
          .statement-text {
            font-size: 8.5pt;
            line-height: 1.5;
            margin-bottom: 10px;
            color: #333;
          }
          .bullet-list {
            margin-left: 15px;
            margin-bottom: 10px;
          }
          .bullet-item {
            font-size: 8.5pt;
            line-height: 1.5;
            margin-bottom: 5px;
          }
          .checkbox-group {
            display: flex;
            align-items: center;
            gap: 6px;
            margin-bottom: 6px;
            font-size: 8.5pt;
          }
          .checkbox-label {
            color: #495057;
          }
          .checkbox-inline {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            margin-right: 12px;
            font-size: 8.5pt;
          }
          .two-column {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
            margin-bottom: 10px;
          }
          .signature-row {
            display: flex;
            gap: 20px;
            margin-top: 10px;
            margin-bottom: 10px;
          }
          .signature-field {
            flex: 1;
          }
          .signature-label {
            font-weight: 600;
            color: #495057;
            margin-bottom: 3px;
            font-size: 8.5pt;
          }
          .signature-value {
            border-bottom: 1px solid #495057;
            min-height: 20px;
            padding-bottom: 2px;
            color: #212529;
            font-size: 8.5pt;
          }
          input[type="checkbox"], input[type="radio"] {
            width: 12px;
            height: 12px;
            margin: 0;
            vertical-align: middle;
          }
          .indent {
            margin-left: 20px;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo-container">
            <img src="/logo.png.png" alt="Kornivus" style="height: 90px; width: auto; object-fit: contain; max-width: 150px;" onerror="this.style.display='none';" />
          </div>
          <div class="contact-info">
            <div>Phone: 818-888-7772</div>
            <div>Fax: 818-888-7773</div>
            <div>22151 Ventura Blvd., #201</div>
            <div>Woodland Hills, Ca, 91364</div>
          </div>
          <div class="header-content">
            <h1>FLU VACCINATION PROGRAM</h1>
          </div>
        </div>

        <div class="section">
          <div class="info-row">
            <span class="info-label">CLINICIAN NAME:</span>
            <span class="info-value">${data.clinicianName || "________________"}</span>
          </div>
          <div class="info-row">
            <span class="info-label">TITLE:</span>
            <span class="info-value">${data.clinicianTitle || "________________"}</span>
          </div>
        </div>

        <div class="section">
          <div class="statement-text">
            Infinite Home Health has advised me that I should receive the Influenza (Flu) Vaccine.
          </div>
          <div class="statement-text" style="font-weight: 600; margin-top: 6px;">
            I understand:
          </div>
          <div class="bullet-list">
            <div class="bullet-item">• The purpose of and the need for the recommended vaccine.</div>
            <div class="bullet-item">• The risk and benefits of the recommended vaccine.</div>
            <div class="bullet-item">• If I do not receive that vaccine, the consequences may include increased risk of:</div>
            <div class="indent">
              <div class="bullet-item">→ Getting sick from the illness the vaccine could prevent</div>
              <div class="bullet-item">→ Being hospitalized for heart disease, stroke, pneumonia, or influenza, if I am 65 years of age or older.</div>
              <div class="bullet-item">→ Spreading the disease to others who become ill, be hospitalized, or die as a result</div>
              <div class="bullet-item">→ Death, if I am 65 years of age or older</div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="statement-text">
            Nevertheless, I have decided to refuse/receive the vaccine recommended above by checking the following appropriate box:
          </div>
          
          <div class="checkbox-group">
            <input type="checkbox" ${data.alreadyReceived ? "checked" : ""} disabled>
            <span class="checkbox-label">Already Received Vaccination:</span>
          </div>
          ${data.alreadyReceived
        ? `
            <div class="indent" style="margin-bottom: 6px;">
              <span style="font-size: 8.5pt;">Location: <span style="border-bottom: 1px solid #495057; min-width: 150px; display: inline-block;">${data.alreadyReceivedLocation || ""}</span></span>
              <span style="margin-left: 15px; font-size: 8.5pt;">Date: <span style="border-bottom: 1px solid #495057; min-width: 100px; display: inline-block;">${formatDateShort(data.alreadyReceivedDate) || ""}</span></span>
            </div>
          `
        : ""
      }
          
          <div class="checkbox-group">
            <input type="checkbox" ${data.wouldLikeToReceive ? "checked" : ""} disabled>
            <span class="checkbox-label">Would like to receive Influenza Vaccination through Infinite Home Health (Fee $15.00)</span>
          </div>
          
          <div class="checkbox-group">
            <input type="checkbox" ${data.refuseDecline ? "checked" : ""} disabled>
            <span class="checkbox-label">Refuse/Decline to Receive Influenza Vaccination due to:</span>
          </div>
          ${data.refuseDecline
        ? `
            <div class="indent">
              <div class="checkbox-inline">
                <input type="checkbox" ${data.refuseReasonAllergies ? "checked" : ""} disabled>
                <span>Allergies</span>
              </div>
              <div class="checkbox-inline">
                <input type="checkbox" ${data.refuseReasonIllness ? "checked" : ""} disabled>
                <span>Illness</span>
              </div>
              <div class="checkbox-inline">
                <input type="checkbox" ${data.refuseReasonReligious ? "checked" : ""} disabled>
                <span>Religious</span>
              </div>
              <div class="checkbox-inline">
                <input type="checkbox" ${data.refuseReasonBeliefs ? "checked" : ""} disabled>
                <span>Beliefs</span>
              </div>
              <div class="checkbox-inline">
                <input type="checkbox" ${data.refuseReasonPersonalPreference ? "checked" : ""} disabled>
                <span>Personal Preference</span>
              </div>
              <div class="checkbox-inline">
                <input type="checkbox" ${data.refuseReasonOther ? "checked" : ""} disabled>
                <span>Other: <span style="border-bottom: 1px solid #495057; min-width: 200px; display: inline-block;">${data.refuseReasonOtherText || ""}</span></span>
              </div>
            </div>
          `
        : ""
      }
        </div>

        <div class="section">
          <div class="statement-text" style="margin-bottom: 8px;">
            I know that my failure to follow these recommendations for vaccination may endanger my health or the health of people I come in contact with. I know that, even though I refuse to be vaccinated now, I can change my mind at any time and accept vaccination in the future. I acknowledge that I have read this Flu Vaccination form in its entirety and fully understand it.
          </div>
          <div class="signature-row">
            <div class="signature-field">
              <div class="signature-label">Signature:</div>
              <div class="signature-value">${data.refusalAcknowledgmentSignature || "________________"}</div>
            </div>
            <div class="signature-field">
              <div class="signature-label">Date:</div>
              <div class="signature-value">${formatDateShort(data.refusalAcknowledgmentDate) || "________________"}</div>
            </div>
          </div>
        </div>

        <div class="divider">
          <div class="divider-text">---Office Use Only---</div>
        </div>

        <div class="section-title">.. FLU VACCINATION CONSENT ..</div>

        <div class="section">
          <div class="statement-text" style="margin-bottom: 8px;">
            I consent to the administration of the Influenza Vaccine, and am aware that some people experience pain at the site of the injection and some may experience fever. I testify that I have none of the following conditions: severe allergy to hen's eggs, severe reaction to flu shot in the past, history of Guillain-Barre Syndrome (GBS) in the 6 weeks after getting a flu shot,
          </div>
          
          <div class="two-column">
            <div class="info-row">
              <span class="info-label">Print Name:</span>
              <span class="info-value">${data.printName || "________________"}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Clinician Signature:</span>
              <span class="info-value">${data.clinicianSignature || "________________"}</span>
            </div>
          </div>
          
          <div class="info-row">
            <span class="info-label">Location Providing Influenza Vaccine:</span>
            <span class="info-value">${data.locationProvidingVaccine || "________________"}</span>
          </div>
          
          <div class="two-column">
            <div class="info-row">
              <span class="info-label">Site of Injection:</span>
              <span class="info-value">${data.siteOfInjection || "________________"}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Administered by:</span>
              <div style="display: flex; align-items: center; gap: 8px; flex: 1;">
                <span class="checkbox-inline">
                  <input type="checkbox" ${data.administeredByMD ? "checked" : ""} disabled>
                  <span>MD</span>
                </span>
                <span class="checkbox-inline">
                  <input type="checkbox" ${data.administeredByRN ? "checked" : ""} disabled>
                  <span>RN</span>
                </span>
                <span style="border-bottom: 1px solid #495057; flex: 1; min-height: 18px; padding-bottom: 2px;">${data.administeredByName || ""}</span>
              </div>
            </div>
          </div>
          
          <div class="two-column">
            <div class="info-row">
              <span class="info-label">Lot #:</span>
              <span class="info-value">${data.lotNumber || "________________"}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Expiration Date:</span>
              <span class="info-value">${formatDateShort(data.expirationDate) || "________________"}</span>
            </div>
          </div>
          
          <div class="info-row">
            <span class="info-label">Clinician's Signature (administering vaccination):</span>
            <span class="info-value">${data.clinicianSignature || "________________"}</span>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // Helper function for time formatting (for Route Sheet)
  const formatTimeWithAMPM = (timeString) => {
    if (!timeString) return "";
    try {
      const [hours, minutes] = timeString.split(":");
      if (!hours || !minutes) return timeString;

      const hour24 = parseInt(hours, 10);
      if (isNaN(hour24)) return timeString;

      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      const ampm = hour24 >= 12 ? "PM" : "AM";

      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  // Generate Medicare Non-Coverage Print HTML
  const generateMedicarePrintFormHTML = (data) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Notice of Medicare Non-Coverage - ${data.patientName || "Patient"}</title>
        <style>
          @media print {
            @page { 
              margin: 0.4in;
              size: letter;
            }
            body { 
              margin: 0; 
              padding: 0;
            }
          }
          * {
            box-sizing: border-box;
          }
          body {
            font-family: 'Arial', 'Helvetica', sans-serif;
            font-size: 9pt;
            line-height: 1.4;
            padding: 10px;
            color: #333;
          }
          .header {
            position: relative;
            margin-bottom: 15px;
            padding-bottom: 10px;
            border-bottom: 2px solid #2c3e50;
            min-height: 90px;
          }
          .logo-container {
            position: absolute;
            top: 0;
            left: 0;
          }
          .header-content {
            text-align: center;
            padding-top: 18px;
          }
          .header h1 {
            font-size: 14pt;
            font-weight: bold;
            margin: 0;
            color: #2c3e50;
            text-transform: uppercase;
          }
          .header h2 {
            font-size: 12pt;
            font-weight: bold;
            margin: 5px 0;
            color: #2c3e50;
          }
          .info-section {
            margin-bottom: 12px;
          }
          .info-row {
            display: flex;
            margin-bottom: 8px;
            font-size: 9pt;
          }
          .info-label {
            font-weight: 600;
            color: #495057;
            min-width: 150px;
          }
          .info-value {
            border-bottom: 1px solid #333;
            flex: 1;
            min-height: 18px;
            padding-bottom: 2px;
            color: #212529;
          }
          .section {
            margin-bottom: 15px;
          }
          .section-title {
            font-size: 11pt;
            font-weight: bold;
            margin-bottom: 8px;
            color: #2c3e50;
            text-transform: uppercase;
          }
          .bullet-list {
            margin-left: 20px;
            margin-bottom: 10px;
          }
          .bullet-item {
            margin-bottom: 6px;
            line-height: 1.5;
            font-size: 9pt;
          }
          .divider {
            border-top: 1px solid #333;
            margin: 15px 0;
          }
          .signature-section {
            margin-top: 15px;
            padding-top: 10px;
            border-top: 1px solid #333;
          }
          .signature-row {
            display: flex;
            gap: 20px;
            margin-bottom: 10px;
            font-size: 9pt;
          }
          .signature-field {
            flex: 1;
          }
          .signature-label {
            font-weight: 600;
            color: #495057;
            margin-bottom: 4px;
          }
          .signature-value {
            border-bottom: 1px solid #333;
            min-height: 20px;
            padding-bottom: 3px;
            color: #212529;
          }
          .contact-info {
            margin-top: 10px;
            padding: 8px;
            background-color: #f8f9fa;
            border: 1px solid #ddd;
          }
          .contact-row {
            margin-bottom: 4px;
            font-size: 9pt;
          }
          .contact-label {
            font-weight: 600;
            color: #495057;
            display: inline-block;
            min-width: 120px;
          }
          .page-break {
            page-break-after: always;
          }
          .form-footer {
            margin-top: 20px;
            font-size: 7pt;
            color: #666;
            display: flex;
            justify-content: space-between;
            padding-top: 10px;
            border-top: 1px solid #ddd;
          }
        </style>
      </head>
      <body>
        <!-- PAGE 1 -->
        <div class="page-break">
          <div class="header">
            <div class="logo-container">
              <img src="/logo.png.png" alt="Kornivus" style="height: 90px; width: auto; object-fit: contain; max-width: 150px;" onerror="this.style.display='none';" />
            </div>
            <div class="header-content">
              <h1>Infinite Home Health</h1>
              <h2>Notice of Medicare Non-Coverage</h2>
            </div>
          </div>

          <div class="info-section">
            <div class="info-row">
              <span class="info-label">Patient name:</span>
              <span class="info-value">${data.patientName || "________________"}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Patient number:</span>
              <span class="info-value">${data.patientNumber || "________________"}</span>
            </div>
          </div>

          <div class="section">
            <div class="section-title">The Effective Date Coverage of Your Current Home Health Services Will End:</div>
            <div class="info-row" style="margin-top: 8px;">
              <span class="info-value" style="min-width: 200px; font-weight: bold;">${formatDateShort(data.effectiveDate) || "________________"}</span>
            </div>
            <div class="bullet-list">
              <div class="bullet-item">• Your Medicare provider and/or health plan have determined that Medicare probably will not pay for your current ${data.serviceType || "{insert type}"} services after the effective date indicated above.</div>
              <div class="bullet-item">• You may have to pay for any services you receive after the above date.</div>
            </div>
          </div>

          <div class="divider"></div>

          <div class="section">
            <div class="section-title">Your Right to Appeal This Decision:</div>
            <div class="bullet-list">
              <div class="bullet-item">• You have the right to an immediate, independent medical review (appeal) of the decision to end Medicare coverage of these services. Your services will continue during the appeal.</div>
              <div class="bullet-item">• If you choose to appeal, the independent reviewer will ask for your opinion. The reviewer also will look at your medical records and/or other relevant information. You do not have to prepare anything in writing, but you have the right to do so if you wish.</div>
              <div class="bullet-item">• If you choose to appeal, you and the independent reviewer will each receive a copy of the detailed explanation about why your coverage for services should not continue. You will receive this detailed notice only after you request an appeal.</div>
              <div class="bullet-item">• If you choose to appeal, and the independent reviewer agrees services should no longer be covered after the effective date indicated above;
                <div style="margin-left: 20px; margin-top: 4px;">- Neither Medicare nor your plan will pay for these services after that date.</div>
              </div>
              <div class="bullet-item">• If you stop services no later than the effective date indicated above, you will avoid financial liability.</div>
            </div>
          </div>

          <div class="divider"></div>

          <div class="section">
            <div class="section-title">How to Ask For an Immediate Appeal:</div>
            <div class="bullet-list">
              <div class="bullet-item">• You must make your request to your Quality Improvement Organization (also known as a QIO). A QIO is the independent reviewer authorized by Medicare to review the decision to end these services.</div>
              <div class="bullet-item">• Your request for an immediate appeal should be made as soon as possible, but no later than noon of the day before the effective date indicated above.</div>
              <div class="bullet-item">• The QIO will notify you of its decision as soon as possible, generally no later than two days after the effective date of this notice if you are in Original Medicare. If you are in a Medicare health plan, the QIO generally will notify you of its decision by the effective date of this notice.</div>
              <div class="bullet-item">• Call your QIO at: Health Services Advisory Group, Inc (HSAG) at 1-800-841-1602</div>
              <div class="bullet-item" style="margin-left: 40px;">• See page 2 of this notice for more information.</div>
            </div>
          </div>

          <div class="form-footer">
            <span>Form CMS 10123-NOMNC (Approved 12/31/2011)</span>
            <span>OMB approval 0938-0963</span>
          </div>
        </div>

        <!-- PAGE 2 -->
        <div>
          <div class="header">
            <h2>If You Miss The Deadline to Request An Immediate Appeal, You May Have Other Appeal Rights:</h2>
          </div>

          <div class="section">
            <div class="bullet-list">
              <div class="bullet-item">• If you have Original Medicare: Call the QIO listed on page 1.</div>
              <div class="bullet-item">• If you belong to a Medicare health plan: Call your plan at the number given below.</div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Plan contact information:</div>
            <div class="contact-info">
              <div class="contact-row">
                <span class="contact-label">Organization Name:</span>
                <span>Kaiser Foundation Health Plan, Inc</span>
              </div>
              <div class="contact-row">
                <span class="contact-label">Attention:</span>
                <span>Attn.: Expedited Appeals</span>
              </div>
              <div class="contact-row">
                <span class="contact-label">Toll Free Telephone:</span>
                <span>1-888-987-7247</span>
              </div>
              <div class="contact-row">
                <span class="contact-label">TTY Number:</span>
                <span>1-800-777-1370</span>
              </div>
              <div class="contact-row">
                <span class="contact-label">Toll Free Fax:</span>
                <span>1-888-987-2252</span>
              </div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Additional Information (Optional):</div>
            <div style="min-height: 80px; border: 1px solid #ddd; padding: 8px; margin-top: 8px;">
              ${data.additionalInformation || ""}
            </div>
          </div>

          <div class="signature-section">
            <div style="margin-bottom: 10px; font-size: 9pt;">
              Please sign below to indicate you received and understood this notice.
            </div>
            <div style="margin-bottom: 10px; font-size: 9pt; line-height: 1.5;">
              I have been notified that coverage of my services will end on the effective date indicated on this notice and that I may appeal this decision by contacting my QIO.
            </div>
            <div class="signature-row">
              <div class="signature-field">
                <div class="signature-label">Signature of Patient or Representative:</div>
                <div class="signature-value">${data.patientSignature || "________________________________________________________________________________"}</div>
              </div>
            </div>
            <div class="signature-row">
              <div class="signature-field">
                <div class="signature-label">Date:</div>
                <div class="signature-value">${formatDateUS(data.signatureDate) || "________________"}</div>
              </div>
            </div>
          </div>

          <div style="margin-top: 20px; font-size: 7pt; color: #666; display: flex; justify-content: space-between;">
            <span>Form CMS 10123-NOMNC (Approved 12/31/2011)</span>
            <span>OMB approval 0938-0963</span>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // Generate Route Sheet Print HTML
  const generateRoutePrintFormHTML = (data) => {
    // Ensure logEntries is an array
    const logEntries = Array.isArray(data.logEntries) ? data.logEntries : [];

    // Format times with AM/PM before generating HTML
    const formattedEntries = logEntries.map((entry) => ({
      ...entry,
      timeInFormatted: formatTimeWithAMPM(entry.timeIn),
      timeOutFormatted: formatTimeWithAMPM(entry.timeOut),
    }));

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Time & Travel Log - ${data.employeeName || "Employee"}</title>
        <style>
          @media print {
            @page { 
              margin: 0.3in;
              size: letter landscape;
            }
            body { 
              margin: 0; 
              padding: 0;
            }
          }
          * {
            box-sizing: border-box;
          }
          body {
            font-family: 'Arial', 'Helvetica', sans-serif;
            font-size: 7.5pt;
            line-height: 1.2;
            padding: 10px;
            color: #333;
          }
          .header {
            position: relative;
            margin-bottom: 12px;
            padding-bottom: 8px;
            border-bottom: 2px solid #2c3e50;
            min-height: 75px;
          }
          .logo-container {
            position: absolute;
            top: 0;
            left: 0;
          }
          .header-content {
            text-align: center;
            padding-top: 18px;
          }
          .header h1 {
            font-size: 20pt;
            font-weight: bold;
            font-style: italic;
            margin: 0;
            color: #2c3e50;
            letter-spacing: 1px;
            text-transform: uppercase;
          }
          .contact-info {
            position: absolute;
            top: 0;
            right: 0;
            font-size: 7pt;
            text-align: right;
            line-height: 1.3;
          }
          .instructions-box {
            border: 2px solid #333;
            padding: 8px 10px;
            margin: 10px 0;
            background-color: #f9f9f9;
            font-size: 7.5pt;
            line-height: 1.4;
          }
          .patient-info {
            display: flex;
            gap: 25px;
            margin-bottom: 12px;
            font-size: 8.5pt;
          }
          .patient-field {
            display: flex;
            align-items: center;
            gap: 6px;
          }
          .patient-label {
            font-weight: 600;
            color: #495057;
            white-space: nowrap;
          }
          .patient-value {
            border-bottom: 1px solid #333;
            min-width: 200px;
            min-height: 16px;
            padding-bottom: 2px;
          }
          .log-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 12px;
            font-size: 7.5pt;
          }
          .log-table th {
            border: 1px solid #333;
            padding: 6px 4px;
            background-color: #f0f0f0;
            font-weight: bold;
            text-align: center;
            vertical-align: middle;
          }
          .log-table td {
            border: 1px solid #333;
            padding: 5px 4px;
            text-align: center;
            vertical-align: middle;
            min-height: 22px;
          }
          .log-table .col-qi {
            width: 3%;
          }
          .log-table .col-date {
            width: 10%;
          }
          .log-table .col-signature {
            width: 25%;
          }
          .log-table .col-code {
            width: 8%;
          }
          .log-table .col-time {
            width: 12%;
          }
          .log-table .col-mileage {
            width: 8%;
          }
          .sub-label {
            font-size: 6.5pt;
            font-weight: normal;
            display: block;
            margin-top: 3px;
          }
          .employee-info {
            display: flex;
            gap: 25px;
            margin-bottom: 12px;
            font-size: 8.5pt;
          }
          .employee-field {
            display: flex;
            align-items: center;
            gap: 6px;
          }
          .employee-label {
            font-weight: 600;
            color: #495057;
            white-space: nowrap;
          }
          .employee-value {
            border-bottom: 1px solid #333;
            min-width: 200px;
            min-height: 16px;
            padding-bottom: 2px;
          }
          .codes-section {
            display: flex;
            gap: 18px;
            margin-top: 12px;
            font-size: 7.5pt;
          }
          .codes-box {
            flex: 1;
            border: 1px solid #333;
            padding: 6px;
          }
          .codes-title {
            font-weight: bold;
            margin-bottom: 5px;
            text-align: center;
            border-bottom: 1px solid #333;
            padding-bottom: 4px;
          }
          .codes-list {
            margin-top: 5px;
          }
          .code-item {
            margin-bottom: 4px;
            line-height: 1.4;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo-container">
            <img src="/logo.png.png" alt="Kornivus" style="height: 70px; width: auto; object-fit: contain; max-width: 120px;" onerror="this.style.display='none';" />
          </div>
          <div class="contact-info">
            <div>Phone: 818-888-7772</div>
            <div>Fax: 818-888-7773</div>
            <div>22151 Ventura Blvd., #201</div>
            <div>Woodland Hills, Ca, 91364</div>
          </div>
          <div class="header-content">
            <h1>TIME & TRAVEL LOG</h1>
          </div>
        </div>

        <div class="instructions-box">
          DUE TO MEDICARE CONFIDENTIALITY ISSUES, YOU ARE TO USE ONLY ONE SIGN-IN SHEET PER PATIENT PER WEEK. The same patient can sign multiple times on the same log sheet within the above-mentioned time frame.
        </div>

        <div class="patient-info">
          <div class="patient-field">
            <span class="patient-label">PATIENT'S NAME:</span>
            <span class="patient-value">${data.patientName || "________________"}</span>
          </div>
          <div class="patient-field">
            <span class="patient-label">MR#:</span>
            <span class="patient-value">${data.patientMR || "________________"}</span>
          </div>
        </div>

        <table class="log-table">
          <thead>
            <tr>
              <th class="col-qi">QI</th>
              <th class="col-date">DATE</th>
              <th class="col-signature">PATIENT'S SIGNATURE</th>
              <th class="col-code">CODE<br><span class="sub-label">SEE TABLE BELOW</span></th>
              <th class="col-time">TIME IN<br><span class="sub-label">MARK: AM OR PM</span></th>
              <th class="col-time">TIME OUT<br><span class="sub-label">MARK: AM OR PM</span></th>
              <th class="col-mileage">MILEAGE<br><span class="sub-label">FULL TIME EMPLOYEES ONLY</span></th>
            </tr>
          </thead>
          <tbody>
            ${formattedEntries
        .map(
          (entry, index) => `
              <tr>
                <td>${index + 1}</td>
                <td>${formatDateUS(entry.date) || ""}</td>
                <td>${entry.patientSignature || ""}</td>
                <td>${entry.code || ""}</td>
                <td>${entry.timeInFormatted || ""}</td>
                <td>${entry.timeOutFormatted || ""}</td>
                <td>${entry.mileage || ""}</td>
              </tr>
            `,
        )
        .join("")}
          </tbody>
        </table>

        <div class="employee-info">
          <div class="employee-field">
            <span class="employee-label">EMPLOYEE'S NAME:</span>
            <span class="employee-value">${data.employeeName || "________________"}</span>
          </div>
          <div class="employee-field">
            <span class="employee-label">TITLE:</span>
            <span class="employee-value">${data.employeeTitle || "________________"}</span>
          </div>
          <div class="employee-field">
            <span class="employee-label">SIGNATURE:</span>
            <span class="employee-value">${data.employeeName || "________________"}</span>
          </div>
        </div>

        <div class="codes-section">
          <div class="codes-box">
            <div class="codes-title">BILLABLE VISIT CODES:</div>
            <div class="codes-list">
              <div class="code-item"><strong>E:</strong> Initial Assessment</div>
              <div class="code-item"><strong>RV:</strong> Return Visit</div>
              <div class="code-item"><strong>IV:</strong> IV Infusion Visit</div>
              <div class="code-item"><strong>RC:</strong> Recertification</div>
              <div class="code-item"><strong>RE:</strong> Resumption of Care</div>
              <div class="code-item"><strong>DC:</strong> Discharge</div>
            </div>
          </div>
          <div class="codes-box">
            <div class="codes-title">NON-BILLABLE VISIT CODES:</div>
            <div class="codes-list">
              <div class="code-item"><strong>Ref:</strong> Refused Visit</div>
              <div class="code-item"><strong>NF:</strong> No Answer to Knock</div>
              <div class="code-item"><strong>S:</strong> Supervisory Visit</div>
            </div>
          </div>
          <div class="codes-box">
            <div class="codes-title">OFFICE CODES:</div>
            <div class="codes-list">
              <div class="code-item"><strong>O:</strong> Orientation</div>
              <div class="code-item"><strong>IS:</strong> In-Service</div>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const generatePrintHTML = (submission) => {
    const data = submission.data;

    // Use actual print HTML generation for all forms
    if (submission.formKey === "consentHistory") {
      return generateConsentPrintFormHTML(data);
    }

    if (submission.formKey === "healthHistory") {
      return generateHealthPrintFormHTML(data);
    }

    if (submission.formKey === "tbHistory") {
      return generateTbPrintFormHTML(data);
    }

    if (submission.formKey === "fluHistory") {
      return generateFluPrintFormHTML(data);
    }

    if (submission.formKey === "routeHistory") {
      return generateRoutePrintFormHTML(data);
    }

    if (submission.formKey === "medicareHistory") {
      return generateMedicarePrintFormHTML(data);
    }

    // Fallback for any unknown form types
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${submission.formType}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          .message { text-align: center; margin-top: 50px; color: #666; }
        </style>
      </head>
      <body>
        <div class="message">
          <h2>${submission.formType}</h2>
          <p>Print view for this form type is being updated.</p>
          <p>Form data submitted: ${formatDate(submission.submittedAt)}</p>
        </div>
      </body>
      </html>
    `;
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          Submitted Documents
        </h1>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <input
              type="text"
              placeholder="Search by name, form type, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Filter by Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Form Type
            </label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Forms</option>
              {Object.entries(formTypes).map(([key, name]) => (
                <option key={key} value={key}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Submissions Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {filteredSubmissions.length === 0 ? (
          <div className="p-12 text-center">
            <DocumentsIcon className="mx-auto text-gray-300 w-16 h-16 mb-4" />
            <p className="text-gray-500 text-lg">
              No submitted documents found
            </p>
            <p className="text-gray-400 text-sm mt-2">
              {searchTerm || filterType !== "all"
                ? "Try adjusting your filters"
                : "Forms will appear here once submitted"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Form Type
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Submitted Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSubmissions.map((submission) => (
                  <tr
                    key={submission.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <DocumentsIcon
                          size={18}
                          className="text-blue-500 mr-2"
                        />
                        <span className="text-sm font-medium text-gray-900">
                          {submission.formType}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <UserIcon size={16} className="text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">
                          {submission.employeeName || "N/A"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-600">
                        <CalendarIcon
                          size={16}
                          className="text-gray-400 mr-2"
                        />
                        {formatDate(submission.submittedAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handlePrint(submission)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Print"
                        >
                          <PrintIcon size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(submission)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <DeleteIcon size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Summary */}
        {filteredSubmissions.length > 0 && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Showing <strong>{filteredSubmissions.length}</strong> of{" "}
              <strong>{allSubmissions.length}</strong> submissions
            </p>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && submissionToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <AlertCircleIcon size={24} className="text-red-600" />
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Delete Submission
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Are you sure you want to delete this{" "}
                  <strong>{submissionToDelete.formType}</strong> submission for{" "}
                  <strong>{submissionToDelete.employeeName || "N/A"}</strong>?
                </p>
                <p className="text-xs text-gray-400 mb-6">
                  This action cannot be undone.
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleDeleteCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubmittedDocuments;